// Pure (tiptap-free) mention helpers + tiptap-JSON walking types, extracted from
// UserMention.ts so the main barrel can re-export them without dragging @tiptap/core
// into the eager bundle. The tiptap extension creator (`createUserMentionExtension`)
// stays in UserMention.ts.

export const MENTION_NODE_TYPE = "mention";

export interface MentionAttrs {
    id?: string;
    username: string;
    label?: string;
}

export interface MentionSuggestionItem {
    id: string;
    label?: string;
    username?: string;
    profileImageUrl?: string;
    description?: string;
}

export interface MentionSuggestionConfig {
    char?: string;
    allowSpaces?: boolean;
    items?: (props: { query: string; editor?: unknown }) => MentionSuggestionItem[] | Promise<MentionSuggestionItem[]>;
    command?: (props: { editor: any; range: any; props: MentionSuggestionItem }) => void;
    render?: () => {
        onStart?: (props: any) => void;
        onUpdate?: (props: any) => void;
        onKeyDown?: (props: any) => boolean;
        onExit?: () => void;
    };
    allow?: (props: any) => boolean;
}

export interface TiptapJsonNode {
    type?: string;
    attrs?: Record<string, unknown> | null;
    content?: TiptapJsonNode[] | null;
    text?: string;
    marks?: Array<{
        type?: string;
        attrs?: Record<string, unknown> | null;
    }> | null;
}

export function normalizeMentionUsername(value: unknown): string {
    if (typeof value !== "string") {
        return "";
    }

    return value.trim().replace(/^@+/, "");
}

function normalizeMentionId(value: unknown): string | undefined {
    if (typeof value !== "string") {
        return undefined;
    }

    const trimmed = value.trim();
    return trimmed ? trimmed : undefined;
}

function normalizeMentionLabel(value: unknown): string | undefined {
    if (typeof value !== "string") {
        return undefined;
    }

    const trimmed = value.trim();
    return trimmed ? trimmed : undefined;
}

export function walkTiptapJson(
    node: TiptapJsonNode | null | undefined,
    visit: (node: TiptapJsonNode) => void,
): void {
    if (!node) {
        return;
    }

    visit(node);

    if (!Array.isArray(node.content)) {
        return;
    }

    for (const child of node.content) {
        walkTiptapJson(child, visit);
    }
}

export function isMentionNode(node: TiptapJsonNode | null | undefined): node is TiptapJsonNode {
    return Boolean(node && node.type === MENTION_NODE_TYPE);
}

export function extractMentionAttrsFromRecord(
    attrs?: Record<string, unknown> | null,
    options: { allowLegacyFallback?: boolean } = {},
): MentionAttrs | null {
    if (!attrs) {
        return null;
    }

    const username = normalizeMentionUsername(
        attrs.username ?? (options.allowLegacyFallback ? attrs.id ?? attrs.label : undefined),
    );
    if (!username) {
        return null;
    }

    const id = normalizeMentionId(attrs.id);
    const label = normalizeMentionLabel(attrs.label);

    return {
        ...(id ? { id } : {}),
        username,
        ...(label ? { label } : {}),
    };
}

export function extractMentionAttrs(
    node: TiptapJsonNode | null | undefined,
    options: { allowLegacyFallback?: boolean } = {},
): MentionAttrs | null {
    if (!isMentionNode(node)) {
        return null;
    }

    return extractMentionAttrsFromRecord(node.attrs, options);
}

export function extractMentions(doc: TiptapJsonNode | null | undefined): MentionAttrs[] {
    const mentions: MentionAttrs[] = [];

    walkTiptapJson(doc, (node) => {
        const mention = extractMentionAttrs(node);
        if (mention) {
            mentions.push(mention);
        }
    });

    return mentions;
}

export function countMentions(doc: TiptapJsonNode | null | undefined): number {
    return extractMentions(doc).length;
}

export function getMentionDisplayText(attrs?: Record<string, unknown> | null): string {
    const mention = extractMentionAttrsFromRecord(attrs, { allowLegacyFallback: true });
    const username = mention?.username ?? "";
    return username ? `@${username}` : "@";
}

export function normalizeMentionItem(item: MentionSuggestionItem): MentionSuggestionItem {
    const username = normalizeMentionUsername(item?.username ?? item?.label ?? item?.id);
    const id = normalizeMentionId(item?.id) ?? username;
    const label = normalizeMentionLabel(item?.label) ?? username;

    return {
        ...item,
        id,
        username,
        label,
    };
}
